import dayjs from 'dayjs';

interface FollowUpRecord {
  type?: string;
  patient_name?: string;
  patient_phone?: string;
  follow_up_date?: string;
  reference_id?: string | number;
  remark?: string;
  // Additional data that might be available
  scheduled_datetime?: string;
  service_type?: string;
  treatment_date?: string;
  treatment_name?: string;
  interest?: string;
  source?: string;
}

/**
 * Generate WhatsApp message template based on follow-up type
 */
export const generateWhatsAppTemplate = (record: FollowUpRecord): string => {
  const { type, patient_name, patient_phone, follow_up_date, remark } = record;

  // Format date with time helper (handles UTC dates properly)
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = dayjs(dateString);
      if (!date.isValid()) return 'N/A';
      return date.format('DD MMM YYYY, hh:mm A');
    } catch {
      return 'N/A';
    }
  };

  // Format time only in 12-hour format with AM/PM
  const formatTime = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = dayjs(dateString);
      if (!date.isValid()) return 'N/A';
      return date.format('hh:mm A');
    } catch {
      return 'N/A';
    }
  };    

  // Format date only (without time)
  const formatDateOnly = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = dayjs(dateString);
      if (!date.isValid()) return 'N/A';
      return date.format('DD MMM YYYY');
    } catch {
      return 'N/A';
    }
  };

  const patientName = patient_name || 'Patient';
  const followUpDate = formatDate(follow_up_date);

  switch (type?.toUpperCase()) {
    case 'APPOINTMENT':
      return generateAppointmentTemplate(record, patientName, followUpDate, formatDate, formatDateOnly);

    case 'FEEDBACK':
      return generateFeedbackTemplate(record, patientName, followUpDate, formatDate, formatDateOnly);

    case 'LEAD':
      return generateLeadTemplate(record, patientName, followUpDate, formatDateOnly);

    case 'PATIENT':
      return generatePatientTemplate(record, patientName, followUpDate, formatDateOnly);

    default:
      return generateDefaultTemplate(record, patientName, followUpDate);
  }
};

/**
 * Generate appointment follow-up template
 */
const generateAppointmentTemplate = (
  record: FollowUpRecord,
  patientName: string,
  followUpDate: string,
  formatDate: (date?: string) => string,
  formatDateOnly: (date?: string) => string
): string => {
  const serviceType = record.service_type || 'Service';
  const appointmentDate = formatDateOnly(record.scheduled_datetime);
  
  // Format appointment time in 12-hour format with AM/PM
  let appointmentTime = 'N/A';
  if (record.scheduled_datetime) {
    try {
      const date = dayjs(record.scheduled_datetime);
      if (date.isValid()) {
        appointmentTime = date.format('hh:mm A');
      }
    } catch (error) {
      console.warn('Error formatting appointment time:', error);
    }
  }

  return `Hello ${patientName}! 👋

This is a reminder about your upcoming appointment:

📅 *Appointment Date:* ${appointmentDate}
🕐 *Appointment Time:* ${appointmentTime}
💼 *Service Type:* ${serviceType}
📞 *Follow-up Date:* ${followUpDate}

${record.remark ? `📝 *Note:* ${record.remark}\n` : ''}Please confirm your availability or let us know if you need to reschedule.

Thank you! 🙏`;
};

/**
 * Generate feedback follow-up template
 */
const generateFeedbackTemplate = (
  record: FollowUpRecord,
  patientName: string,
  followUpDate: string,
  formatDate: (date?: string) => string,
  formatDateOnly: (date?: string) => string
): string => {
  const treatmentDate = formatDateOnly(record.treatment_date || record.scheduled_datetime);
  const treatmentName = record.treatment_name || 'Treatment';

  return `Hello ${patientName}! 👋

We hope you're doing well after your recent visit. We'd love to hear your feedback:

💉 *Treatment:* ${treatmentName}
📅 *Treatment Date:* ${treatmentDate}
📞 *Follow-up Date:* ${followUpDate}

${record.remark ? `📝 *Note:* ${record.remark}\n` : ''}Your feedback helps us improve our services. Please share your experience with us.

Thank you! 🙏`;
};

/**
 * Generate lead follow-up template
 */
const generateLeadTemplate = (
  record: FollowUpRecord,
  patientName: string,
  followUpDate: string,
  formatDateOnly: (date?: string) => string
): string => {
  const interest = record.interest || 'our services';
  const source = record.source ? ` (via ${record.source})` : '';

  return `Hello ${patientName}! 👋

Thank you for your interest in ${interest}${source}. We'd like to follow up with you:

📞 *Follow-up Date:* ${followUpDate}
${record.remark ? `📝 *Note:* ${record.remark}\n` : ''}We're here to answer any questions you may have and help you with your needs.

Please let us know a convenient time to connect.

Thank you! 🙏`;
};

/**
 * Generate patient follow-up template
 */
const generatePatientTemplate = (
  record: FollowUpRecord,
  patientName: string,
  followUpDate: string,
  formatDateOnly: (date?: string) => string
): string => {
  return `Hello ${patientName}! 👋

This is a follow-up regarding your care:

📞 *Follow-up Date:* ${followUpDate}
${record.remark ? `📝 *Note:* ${record.remark}\n` : ''}We're checking in to see how you're doing and if you need any assistance.

Please feel free to reach out if you have any questions or concerns.

Thank you! 🙏`;
};

/**
 * Generate default template for unknown types
 */
const generateDefaultTemplate = (
  record: FollowUpRecord,
  patientName: string,
  followUpDate: string
): string => {
  return `Hello ${patientName}! 👋

This is a follow-up:

📞 *Follow-up Date:* ${followUpDate}
${record.remark ? `📝 *Note:* ${record.remark}\n` : ''}Please let us know if you need any assistance.

Thank you! 🙏`;
};

/**
 * Generate WhatsApp URL with pre-filled message
 */
export const generateWhatsAppUrl = (phone: string, message: string): string => {
  const encodedMessage = encodeURIComponent(message);
  return `https://web.whatsapp.com/send/?phone=${phone}&text=${encodedMessage}`;
};

